import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatNumber,
  formatPercent,
  toNumber,
  clamp
} from './panel-utils.js';

const DEFAULT_CLUSTERS = [
  { id: 'auth', label: 'Auth', load: 0.62, risk: 0.18 },
  { id: 'billing', label: 'Billing', load: 0.48, risk: 0.12 },
  { id: 'search', label: 'Search', load: 0.71, risk: 0.26 },
  { id: 'edge', label: 'Edge', load: 0.54, risk: 0.2 },
  { id: 'ml', label: 'ML', load: 0.43, risk: 0.1 }
];

const DEFAULT_LINKS = [
  { from: 'auth', to: 'billing', strength: 0.62 },
  { from: 'search', to: 'edge', strength: 0.52 },
  { from: 'edge', to: 'ml', strength: 0.38 },
  { from: 'auth', to: 'search', strength: 0.44 }
];

export function render({ node }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Event Correlation Cloud');

  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const canvasWrap = document.createElement('div');
  canvasWrap.className = 'ew-correlation-canvas';
  const canvas = document.createElement('canvas');
  canvas.width = 360;
  canvas.height = 180;
  canvasWrap.appendChild(canvas);

  const footer = document.createElement('div');
  footer.className = 'ew-particle-footer';

  body.append(metrics, canvasWrap, footer);

  const ctx = canvas.getContext('2d');
  const state = {
    particles: [],
    lastTs: 0,
    raf: null,
    resizeObserver: null,
    data: null
  };

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Event Correlation Cloud');
    const data = normalizeData(next);
    state.data = data;

    metaEl.textContent = data.window;

    metrics.innerHTML = '';
    metrics.append(
      createMetric('events', formatNumber(data.eventCount)),
      createMetric('clusters', formatNumber(data.clusters.length)),
      createMetric('risk', formatPercent(data.riskScore, 0)),
      createMetric('links', formatNumber(data.links.length))
    );

    footer.textContent = data.mode === 'alert' ? 'Alert correlation spikes' : 'Live correlation map';

    ensureParticles(state);
    startLoop();
  };

  const startLoop = () => {
    if (state.raf) return;
    const tick = (ts) => {
      state.raf = requestAnimationFrame(tick);
      const delta = state.lastTs ? ts - state.lastTs : 16;
      state.lastTs = ts;
      drawFrame(state, ctx, delta);
    };
    state.raf = requestAnimationFrame(tick);
  };

  const stopLoop = () => {
    if (state.raf) cancelAnimationFrame(state.raf);
    state.raf = null;
  };

  state.resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect;
      canvas.width = Math.max(240, Math.floor(width));
      canvas.height = Math.max(140, Math.floor(height));
    }
  });
  state.resizeObserver.observe(canvasWrap);

  update(node);

  return {
    el: root,
    update,
    cleanup: () => {
      stopLoop();
      state.resizeObserver?.disconnect();
    }
  };
}

function normalizeData(node) {
  const raw = node?.data || node?.props || {};
  const clusters = Array.isArray(raw.clusters) && raw.clusters.length
    ? raw.clusters.map((c, i) => ({
      id: String(c.id ?? `cluster-${i}`),
      label: c.label || c.id || `Cluster ${i + 1}`,
      load: clamp(toNumber(c.load, Math.random()), 0, 1),
      risk: clamp(toNumber(c.risk, 0.1 + Math.random() * 0.2), 0, 1),
      color: c.color || null
    }))
    : DEFAULT_CLUSTERS;

  const links = Array.isArray(raw.links) && raw.links.length
    ? raw.links.map((l) => ({
      from: String(l.from),
      to: String(l.to),
      strength: clamp(toNumber(l.strength, 0.4), 0, 1)
    }))
    : DEFAULT_LINKS;

  const eventCount = toNumber(raw.eventCount, clusters.length * 160);
  const riskScore = clusters.length
    ? clusters.reduce((acc, c) => acc + c.risk, 0) / clusters.length
    : 0;

  return {
    clusters,
    links,
    mode: raw.mode || 'live',
    window: raw.window || '15m',
    eventCount,
    riskScore,
    particleCount: clamp(toNumber(raw.particleCount, 420), 160, 900),
    turbulence: clamp(toNumber(raw.turbulence, 0.35), 0, 1)
  };
}

function ensureParticles(state) {
  const target = state.data?.particleCount || 420;
  while (state.particles.length < target) state.particles.push(spawnParticle(state));
  if (state.particles.length > target) state.particles.length = target;
}

function spawnParticle(state) {
  const clusterIndex = Math.floor(Math.random() * state.data.clusters.length);
  return {
    clusterIndex,
    angle: Math.random() * Math.PI * 2,
    radius: 0.1 + Math.random() * 0.4,
    speed: 0.4 + Math.random() * 0.8,
    drift: Math.random() * 0.4
  };
}

function drawFrame(state, ctx, delta) {
  if (!state.data) return;
  const { clusters, links, turbulence, mode } = state.data;
  const width = ctx.canvas.width;
  const height = ctx.canvas.height;

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = 'rgba(5, 18, 30, 0.85)';
  ctx.fillRect(0, 0, width, height);

  const centers = layoutClusters(clusters, width, height);

  drawLinks(ctx, links, centers, mode);
  drawClusters(ctx, clusters, centers);

  state.particles.forEach((p) => {
    const cluster = clusters[p.clusterIndex % clusters.length];
    const center = centers[cluster.id];
    p.angle += (p.speed * delta) / 1600;
    const jitter = (Math.random() - 0.5) * turbulence * 0.08;
    const radius = clamp(p.radius + jitter, 0.08, 0.48);
    const x = center.x + Math.cos(p.angle) * radius * center.r;
    const y = center.y + Math.sin(p.angle) * radius * center.r;
    const intensity = 0.3 + cluster.load * 0.7;
    if (cluster.color) {
      ctx.fillStyle = cluster.color;
      ctx.globalAlpha = 0.3 + intensity * 0.5;
    } else {
      ctx.fillStyle = `rgba(${Math.floor(80 + cluster.load * 120)}, ${Math.floor(180 + cluster.risk * 60)}, 255, ${0.2 + intensity * 0.6})`;
      ctx.globalAlpha = 1;
    }
    ctx.beginPath();
    ctx.arc(x, y, 1.1 + intensity * 1.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  });
}

function layoutClusters(clusters, width, height) {
  const cx = width / 2;
  const cy = height / 2;
  const ring = Math.min(width, height) * 0.32;
  const result = {};
  const count = clusters.length || 1;
  clusters.forEach((cluster, idx) => {
    const angle = (Math.PI * 2 * idx) / count - Math.PI / 2;
    const r = ring * (0.8 + cluster.load * 0.3);
    result[cluster.id] = {
      x: cx + Math.cos(angle) * ring,
      y: cy + Math.sin(angle) * ring * 0.8,
      r
    };
  });
  return result;
}

function drawLinks(ctx, links, centers, mode) {
  links.forEach((link) => {
    const from = centers[link.from];
    const to = centers[link.to];
    if (!from || !to) return;
    const strength = clamp(link.strength, 0, 1);
    ctx.strokeStyle = mode === 'alert'
      ? `rgba(255, 140, 140, ${0.2 + strength * 0.5})`
      : `rgba(95, 234, 255, ${0.12 + strength * 0.4})`;
    ctx.lineWidth = 1 + strength * 1.2;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  });
}

function drawClusters(ctx, clusters, centers) {
  clusters.forEach((cluster) => {
    const center = centers[cluster.id];
    const glow = 6 + cluster.load * 12;
    ctx.strokeStyle = 'rgba(95, 234, 255, 0.25)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(center.x, center.y, glow, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = 'rgba(234, 255, 255, 0.75)';
    ctx.font = '10px "Share Tech Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillText(cluster.label, center.x, center.y - glow - 4);
  });
}
